// The app is always called at its own origin — localhost:5173 in `npm run dev`
// (proxied by Vite, see vite.config.ts), localhost:8090 under `docker compose`,
// and tecoapp.io in production. In every case /api/* is same-origin and gets
// routed to the right backend by whichever reverse proxy is in front (Vite's
// dev proxy locally, nginx everywhere else) — the frontend never needs to know
// mzito's or mzito-pay's actual URL.
export const API_CONFIG = {
  baseUrl: "",
  paymentUrl: "",
  timeout: 30000, // 30 seconds — allows for cold-start on hosted servers
  retryAttempts: 3,
};

// Utility functions
const createHeaders = (additionalHeaders = {}) => {
  const headers = new Headers({
    "Content-Type": "application/json",
    ...additionalHeaders,
  });
  return headers;
};

const handleResponse = async (response) => {
  if (!response.ok) {
    let message = `HTTP error! status: ${response.status}`;
    let code;
    try {
      const errorData = await response.json();
      if (errorData.message) message = errorData.message;
      if (errorData.code) code = errorData.code;
    } catch {
      // response body wasn't JSON — keep the default message
    }
    const error = new Error(message);
    if (code) error.code = code;
    throw error;
  }

  try {
    const text = await response.text();
    // If the response is empty, return an empty object
    if (!text) return {};
    return JSON.parse(text);
  } catch (e) {
    console.error("Error parsing response:", e);
    throw new Error("Failed to parse server response");
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Backend connections can be momentarily unavailable right after startup
// (local dev racing the backend's boot, or a hosted service waking from a
// cold start) — a fast ECONNREFUSED/ECONNRESET fails well before the 30s
// per-attempt timeout even matters. Retrying instantly just re-hits the same
// not-yet-ready backend, so back off (capped, with jitter to avoid retries
// from concurrent requests all landing on the same instant) before each
// retry to give it a moment to come up.
const RETRY_BASE_DELAY = 250; // ms
const RETRY_MAX_DELAY = 2000; // ms

const fetchWithRetry = async (
  url,
  options,
  retries = API_CONFIG.retryAttempts,
  attempt = 0
) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    if (retries > 0) {
      const delay =
        Math.min(RETRY_BASE_DELAY * 2 ** attempt, RETRY_MAX_DELAY) +
        Math.random() * RETRY_BASE_DELAY;
      await sleep(delay);
      return fetchWithRetry(url, options, retries - 1, attempt + 1);
    }
    throw error.name === "AbortError" ? new Error("Request timeout") : error;
  }
};

// API Functions
/**
 * Get average orders value for a store by storeId
 * @param {string} storeId - The store's unique identifier
 * @returns {Promise<number>} Average value
 */
export const getOrdersAverageByStoreId = async (storeId, period, token) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/orders/average?storeId=${storeId}&period=${period}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Get total orders count for a store by storeId
 * @param {string} storeId - The store's unique identifier
 * @returns {Promise<number>} Order count
 */
export const getOrdersCountByStoreId = async (storeId, period, token) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/orders/count?storeId=${storeId}&period=${period}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Get total revenue for a store by storeId
 * @param {string} storeId - The store's unique identifier
 * @returns {Promise<number>} Total revenue
 */
export const getTotalRevenue = async (storeId, period, token) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/revenue?storeId=${storeId}&period=${period}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Get revenue breakdown for a store for a specific period
 * @param {string} storeId - The store's unique identifier
 * @param {string} period - The period (e.g., 'week', 'month', 'year')
 * @returns {Promise<Object[]>} Array of key-value pairs (e.g., [{ date: '2024-07-21', revenue: 1234 }])
 */
export const getRevenueForPeriod = async (storeId, period, token) => {
  if (!storeId || !period) throw new Error("Store ID and period are required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/revenue/period?storeId=${storeId}&period=${period}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Get orders status average for a store by storeId and period
 * @param {string} storeId - The store's unique identifier
 * @param {string} period - The time period (week, month, year)
 * @returns {Promise<Object>} Object containing statusDistribution and statusCounts
 */
export const getOrdersStatusAverage = async (storeId, period, token) => {
  if (!storeId || !period) throw new Error("Store ID and period are required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/status/average?storeId=${storeId}&period=${encodeURIComponent(period)}`;

  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Get active orders for a store by storeId
 * @param {string} storeId - The store's unique identifier
 * @returns {Promise<Object[]>} Array of key-value pairs representing active orders
 */
export const getActiveOrders = async (storeId, token) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/orders/active?storeId=${storeId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Get order history for a store by storeId and status
 * @param {string} storeId - The store's unique identifier
 * @param {string} status - The order status to filter by (e.g., 'delivered', 'cancelled')
 * @returns {Promise<Object[]>} Array of key-value pairs representing order history
 */
export const getMerchantOrderHistory = async (storeId, filter, token) => {
  if (!storeId || !filter) throw new Error("Store ID and filter are required");
  if (!token) throw new Error("Auth token is required");
  const url = `${API_CONFIG.baseUrl}/api/v1/merchant/orders/history?storeId=${storeId}&filter=${filter}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Fetch all stores
 * @returns {Promise<Object[]>} Array of store objects
 */
export const fetchStores = async () => {
  const url = `${API_CONFIG.baseUrl}/api/v1/stores/`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  const data = await handleResponse(response);
  return data;
};

/**
 * Fetch stores by category
 * @param {string} category - Store category
 * @returns {Promise<Object[]>} Array of store objects
 */
export const fetchStoresByCategory = async (category) => {
  if (!category) throw new Error("Category is required");

  const url = `${
    API_CONFIG.baseUrl
  }/api/v1/stores/category?cat=${encodeURIComponent(category)}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch store info by storeId
 * @param {string} storeId - Store ID
 * @returns {Promise<Object>} Store info object
 */
export const fetchStoreInfo = async (storeId) => {
  if (!storeId) throw new Error("Store ID is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/stores/${storeId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch store catalog by storeId
 * @param {string} storeId - Store ID
 * @returns {Promise<Object[]>} Array of product objects
 */
export const fetchStoreCatalog = async (storeId) => {
  if (!storeId) throw new Error("Store ID is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/filterByStore?id=${storeId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch liquor store catalog by storeId, type, and subType
 * @param {string} storeId - Store ID
 * @param {string} type - Liquor type
 * @param {string} subType - Liquor sub-type
 * @returns {Promise<Object[]>} Array of liquor product objects
 */
export const fetchLiquorStoreCatalog = async (storeId, type, subType) => {
  if (!storeId || !type || !subType)
    throw new Error("Store ID, type, and subType are required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/liquor/${storeId}/${type}/${subType}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch product by productId
 * @param {string} productId - Product ID
 * @returns {Promise<Object>} Product object
 */
export const fetchProduct = async (productId) => {
  if (!productId) throw new Error("Product ID is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${productId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Edit product details
 * @param {Object} product - Product data
 * @returns {Promise<string>} Status string
 */
export const editProduct = async (product, token) => {
  if (!product) throw new Error("Product data is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/product/edit`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(product),
  });
  const data = await handleResponse(response);

  if (data.status === "failed") {
    throw new Error("Product edit could not be completed");
  }
  return data.status;
};

/**
 * Fetch product options by productId
 * @param {string} productId - Product ID
 * @returns {Promise<Object[]>} Array of option objects
 */
export const fetchProductOptions = async (productId) => {
  if (!productId) throw new Error("Product ID is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${productId}/options`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch product option list by productId and optionId
 * @param {string} productId - Product ID
 * @param {string} optionId - Option ID
 * @returns {Promise<Object[]>} Array of option list items
 */
export const fetchProductOptionList = async (productId, optionId) => {
  if (!productId || !optionId)
    throw new Error("Product ID and Option ID are required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${productId}/options/${optionId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch product option item by productId, optionId, and itemId
 * @param {string} productId - Product ID
 * @param {string} optionId - Option ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} Option item object
 */
export const fetchProductOptionItem = async (productId, optionId, itemId) => {
  if (!productId || !optionId || !itemId) {
    throw new Error("Product ID, Option ID, and Item ID are required");
  }

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${productId}/options/${optionId}/list/${itemId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Register a new customer
 * @param {string} email - Customer email
 * @param {string} password - Customer password
 * @returns {Promise<Object>} Registration response
 */
export const registerCustomer = async (email, password) => {
  if (!email || !password) throw new Error("Email and password are required");

  const url = `${API_CONFIG.baseUrl}/api/v1/auth/register`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify({ email, password }),
  });
  return handleResponse(response);
};

/**
 * Send email verification
 * @param {string} email - Recipient email
 * @param {string} link - Verification link
 * @returns {Promise<Object>} Verification response
 */
export const mailVerification = async (email, link) => {
  if (!email || !link)
    throw new Error("Email and verification link are required");

  const url = `${API_CONFIG.baseUrl}/api/v1/auth/mail/verify`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify({ recipient: email, actionLink: link }),
  });
  return handleResponse(response);
};

/**
 * Request password reset link
 * @param {string} email - User email
 * @returns {Promise<Object>} Reset link response
 */
export const resetPasswordLink = async (email) => {
  if (!email) throw new Error("Email is required");

  const url = `${
    API_CONFIG.baseUrl
  }/api/v1/auth/reset/link?email=${encodeURIComponent(email)}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Get whether Teco is currently open for checkout, and the configured hours.
 * @returns {Promise<{enabled: boolean, isOpen: boolean, opensAt: string, closesAt: string, timezone: string}>}
 */
export const getServiceStatus = async () => {
  const url = `${API_CONFIG.baseUrl}/api/v1/service-status`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Place a new order
 * @param {Object} payload - Order payload
 * @returns {Promise<Object>} Order response
 */
export const placeOrder = async (payload, token) => {
  if (!payload) throw new Error("Order payload is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/order`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Send password reset email
 * @param {string} email - Recipient email
 * @param {string} link - Reset link
 * @returns {Promise<Object>} Reset email response
 */
export const resetPasswordMail = async (email, link) => {
  if (!email || !link) throw new Error("Email and reset link are required");

  const url = `${API_CONFIG.baseUrl}/api/v1/auth/mail/reset`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify({ recipient: email, actionLink: link }),
  });
  return handleResponse(response);
};

/**
 * Register an elite user
 * @param {Object} body - Registration data
 * @returns {Promise<Object>} Registration response
 */
export const registerElit = async (body, token) => {
  if (!body) throw new Error("Registration data is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/sup/register`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Register a merchant
 * @param {Object} body - Registration data
 * @returns {Promise<Object>} Registration response
 */
export const registerStore = async (body) => {
  if (!body) throw new Error("Merchant registration data is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/auth/store/register`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Register a rider
 * @param {Object} body - Registration data
 * @returns {Promise<Object>} Registration response
 */
export const registerRider = async (body) => {
  if (!body) throw new Error("Registration data is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/operations/register`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders(),
    body: JSON.stringify(body),
  });
  return handleResponse(response);
};

/**
 * Accept an order
 * @param {string} id - Order ID
 * @param {string} uid - User ID
 * @param {Object} order - Order data
 * @returns {Promise<Object>} Accept response
 */
export const acceptOrder = async (id, uid, order, token) => {
  if (!id || !uid || !order)
    throw new Error("Order ID, User ID, and order data are required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/order/accept?id=${id}&rid=${uid}`;
  // eslint-disable-next-line no-unused-vars
  const { createdAt, updatedAt, ...orderPayload } = order;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(orderPayload),
  });
  return handleResponse(response);
};

/**
 * Fetch active orders for a rider
 * @param {string} rid - Rider ID
 * @returns {Promise<Object[]>} Array of active orders
 */
export const fetchActiveOrders = async (rid, token) => {
  if (!rid) throw new Error("Rider ID is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/active?rid=${rid}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Fetch option items for a product
 * @param {string} id - Product ID
 * @param {Array} selectedOptions - Selected options array
 * @returns {Promise<Object[]>} Array of option items
 */
export const fetchOptionItems = async (id, selectedOptions) => {
  if (!id || !selectedOptions)
    throw new Error("Product ID and selected options are required");

  const options = [];
  for (const option of selectedOptions) {
    if (option.optionId) {
      const url = `${API_CONFIG.baseUrl}/api/v1/products/${id}/options/${option.optionId}/list/${option.optionItemId}`;
      const response = await fetchWithRetry(url, {
        method: "GET",
        headers: createHeaders(),
      });
      const data = await handleResponse(response);
      options.push(data);
    }
  }
  return options;
};

/**
 * Fetch all placed orders
 * @returns {Promise<Object[]>} Array of placed orders
 */
export const fetchPlacedOrders = async (token) => {
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/filterByStatus?status=placed`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Update order status
 * @param {string} status - New status
 * @param {string} orderId - Order ID
 * @param {string} rid - Rider ID
 * @param {string} deliveryId - Delivery ID
 * @param {string} uid - User ID
 * @returns {Promise<Object>} Status update response
 */
export const updateOrderStatus = async (
  status,
  orderId,
  rid,
  deliveryId,
  uid,
  token
) => {
  if (!status || !orderId || !rid || !deliveryId || !uid) {
    throw new Error("All parameters are required for status update");
  }
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/order/active/statusUpdate?status=${status}&orderId=${orderId}&rid=${rid}&deliveryId=${deliveryId}&uid=${uid}`;
  const response = await fetchWithRetry(url, {
    method: "PUT",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Fetch active delivery by ID
 * @param {string} id - Delivery ID
 * @param {string} rid - Rider ID
 * @returns {Promise<Object>} Delivery object
 */
export const fetchActiveDeliveryById = async (id, rid, token) => {
  if (!id || !rid) throw new Error("Delivery ID and Rider ID are required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/order/active?rid=${rid}&id=${id}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Fetch completed deliveries
 * @param {string} rid - Rider ID
 * @param {string} id - Delivery ID
 * @returns {Promise<Object[]>} Array of completed deliveries
 */
export const fetchCompletedDeliveries = async (rid, id, token) => {
  if (!rid || !id) throw new Error("Rider ID and Delivery ID are required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/order/active?rid=${rid}&id=${id}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Fetch order by ID
 * @param {string} id - Order ID
 * @returns {Promise<Object>} Order object
 */
export const fetchOrderById = async (id, token) => {
  if (!id) throw new Error("Order ID is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/${id}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Update active order status
 * @param {string} status - New status
 * @param {string} orderId - Order ID
 * @param {string} rid - Rider ID
 * @param {string} deliveryId - Delivery ID
 * @param {string} uid - User ID
 * @returns {Promise<Object>} Status update response
 */
export const updateActiveStatus = async (
  status,
  orderId,
  rid,
  deliveryId,
  uid,
  token
) => {
  if (!status || !orderId || !rid || !deliveryId || !uid) {
    throw new Error("All parameters are required for status update");
  }
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/order/active/status/update?status=${status}&orderId=${orderId}&rid=${rid}&deliveryId=${deliveryId}&uid=${uid}`;
  const response = await fetchWithRetry(url, {
    method: "PUT",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Fetch delivered orders for a rider
 * @param {string} rid - Rider ID
 * @returns {Promise<Object[]>} Array of delivered orders
 */
export const fetchDeliveredOrders = async (rid, token) => {
  if (!rid) throw new Error("Rider ID is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/delivered/${rid}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Fetch all orders
 * @returns {Promise<Object[]>} Array of all orders
 */
export const fetchAllOrders = async (token) => {
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/docs`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Delete a list item from a product option
 * @param {string} productId - Product ID
 * @param {string} optionId - Option ID
 * @param {string} id - Item ID
 * @returns {Promise<Object>} Delete response
 */
export const deleteListItem = async (productId, optionId, id, token) => {
  if (!productId || !optionId || !id) {
    throw new Error("Product ID, Option ID, and Item ID are required");
  }
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${productId}/options/${optionId}/list/${id}/delete`;
  const response = await fetchWithRetry(url, {
    method: "DELETE",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const data = await handleResponse(response);
  if (data.status === "failed") {
    throw new Error("Item delete could not be completed");
  }
  return data.status;
};

/**
 * Update a product option list item
 * @param {string} productId - Product ID
 * @param {string} optionId - Option ID
 * @param {string} id - Item ID
 * @param {Object} item - Item data
 * @returns {Promise<Object>} Update response
 */
export const updateOptionListItem = async (productId, optionId, id, item, token) => {
  if (!productId || !optionId || !id || !item) {
    throw new Error("All parameters are required for update");
  }
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${productId}/options/${optionId}/list/${id}/update`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(item),
  });
  const data = await handleResponse(response);
  if (data.status === "failed") {
    throw new Error("Item update could not be completed");
  }
  return data.status;
};

/**
 * Edit a product option
 * @param {Object} option - Option data
 * @param {string} pid - Product ID
 * @param {string} oid - Option ID
 * @returns {Promise<Object>} Edit response
 */
export const editProductOption = async (option, pid, oid, token) => {
  if (!option || !pid || !oid) {
    throw new Error("Option data, Product ID, and Option ID are required");
  }
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/${pid}/options/${oid}/update`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(option),
  });
  const data = await handleResponse(response);
  if (data.status === "failed") {
    throw new Error("Option update could not be completed");
  }
  return data.status;
};

/**
 * Add a new product
 * @param {Object} product - Product data
 * @returns {Promise<Object>} Add response
 */
export const addNewProduct = async (product, token) => {
  if (!product) throw new Error("Product data is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/add`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(product),
  });
  return handleResponse(response);
};

/**
 * Add option list items to a product
 * @param {Object} items - Items data
 * @param {string} pid - Product ID
 * @param {string} oid - Option ID
 * @returns {Promise<Object>} Add response
 */
export const addOptionListItem = async (items, pid, oid, token) => {
  if (!items || !pid || !oid) {
    throw new Error("Items data, Product ID, and Option ID are required");
  }
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/options/list/add?pid=${pid}&oid=${oid}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(items),
  });
  return handleResponse(response);
};

/**
 * Save a product option
 * @param {Object} option - Option data
 * @param {string} pid - Product ID
 * @returns {Promise<Object>} Save response
 */
export const saveProductOption = async (option, pid, token) => {
  if (!option || !pid)
    throw new Error("Option data and Product ID are required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/products/options/add?pid=${pid}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(option),
  });
  return handleResponse(response);
};

/**
 * Fetch order history for a user
 * @param {string} uid - User ID
 * @returns {Promise<Object[]>} Array of order history objects
 */
export const fetchOrdersHistory = async (uid, token) => {
  if (!uid) throw new Error("User ID is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/history?uid=${uid}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Parse a Kenya Google Maps address string into { county, constituency }.
 * Expected format: "Location, Constituency, County, Kenya"
 * Falls back to "Meru" for both when the string is too short to parse.
 */
const parseKenyanAddress = (text) => {
  const FALLBACK = "Meru";
  if (!text) return { county: FALLBACK, constituency: FALLBACK };
  const parts = text.split(",").map((s) => s.trim()).filter(Boolean);
  // parts[-1] = "Kenya", parts[-2] = county, parts[-3] = constituency
  const county       = (parts[parts.length - 2] || FALLBACK).slice(0, 50);
  const constituency = (parts[parts.length - 3] || county).slice(0, 50);
  return {
    county:       county       || FALLBACK,
    constituency: constituency || FALLBACK,
  };
};

/**
 * Initiate payment for an order via /api/payments/process-delivery.
 * Maps the checkout order shape → OrderPaymentRequest, calls the new endpoint,
 * and returns { success, state, invoice_id, mpesa_reference, order_id, transaction_id }.
 *
 * @param {Object} order_data - Checkout order (firstName, lastName, phone, phoneTrans,
 *   buildingType, unitFloor, buildingName, location, cart, cost, route)
 * @param {string} token - Firebase ID token
 * @param {string} email - Customer email
 */
export const initiatePayment = async (order_data, token, email) => {
  if (!order_data || !token || !email) throw new Error("Payment data, token and email are required");

  const fullName    = `${order_data.firstName || ""} ${order_data.lastName || ""}`.trim();
  const pickupAddr  = parseKenyanAddress(order_data.route?.start);
  const dropoffAddr = parseKenyanAddress(order_data.route?.end);

  const mappedOrder = {
    sender: {
      name:  fullName,
      email,
      phone: order_data.phone,
    },
    recipient: {
      name:  fullName,
      phone: order_data.phone,
    },
    pickup: {
      county:       pickupAddr.county,
      constituency: pickupAddr.constituency,
      location:     (order_data.route?.start || "Store").slice(0, 100),
    },
    dropoff: {
      county:        dropoffAddr.county,
      constituency:  dropoffAddr.constituency,
      location:      (order_data.route?.end || order_data.buildingName || "Delivery address").slice(0, 100),
      estate:        order_data.buildingName || null,
      landmark:      [order_data.buildingType, order_data.unitFloor].filter(Boolean).join(" – ") || null,
      coordinates:   order_data.location ?? null,
      buildingType:  order_data.buildingType || null,
      unitFloor:     order_data.unitFloor    || null,
    },
    items: (order_data.cart || []).map((item) => ({
      description: String(item.id).slice(0, 200),
      quantity:    item.quantity,
    })),
    cart:  order_data.cart || [],
    route: order_data.route ?? null,
    payment: {
      method: "mpesa",
      phone:  order_data.phoneTrans || order_data.phone || null,
    },
    // Optimistic preview only — mzito-pay recomputes service fee, discounts
    // and total from sub_total/delivery_fee/tax before charging.
    totals: {
      sub_total:    order_data.cost?.subTotal     ?? 0,
      service_fee:  order_data.cost?.serviceFee   ?? 0,
      delivery_fee: order_data.cost?.deliveryFee ?? 0,
      tax:          order_data.cost?.tax          ?? 0,
      total:        order_data.cost?.total        ?? 0,
    },
  };

  const url        = `${API_CONFIG.paymentUrl}/api/payments/process-delivery`;
  const controller = new AbortController();
  const timeoutId  = setTimeout(() => controller.abort(), 30000); // 30s — server returns immediately with PENDING

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: createHeaders({ Authorization: `Bearer ${token}` }),
      body: JSON.stringify({ order: mappedOrder }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    const text = await response.text();
    const data = text ? JSON.parse(text) : {};

    if (!response.ok) {
      const error = new Error(data.failed_reason || data.message || `HTTP error! status: ${response.status}`);
      if (data.code) error.code = data.code;
      throw error;
    }

    // Server wraps payload: { success, message, data: { invoice_id, state, amount } }
    return data.data || data;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") throw new Error("Payment request timeout");
    throw error;
  }
};

/**
 * Poll the payment status for an invoice.
 * @param {string} invoiceId - Invoice ID returned by initiatePayment
 * @param {string} token - Auth token
 * @returns {Promise<{invoice_id, state, amount, mpesa_reference, failed_reason}>}
 */
export const getPaymentStatus = async (invoiceId, token) => {
  if (!invoiceId || !token) throw new Error("Invoice ID and token are required");
  const url = `${API_CONFIG.paymentUrl}/api/payments/status/${invoiceId}`;
  const response = await fetch(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new Error(data.message || `HTTP error! status: ${response.status}`);
  }
  return data.data || data;
};

/**
 * Fetch the signed-in customer's referral code, wallet balance, and stats.
 * Lazily creates a referral code server-side on first call.
 * @param {string} token - Firebase ID token
 * @returns {Promise<{code, walletBalance, referralsCount, rewardsEarned, canApplyCode}>}
 */
export const fetchReferralSummary = async (token) => {
  if (!token) throw new Error("Token is required");
  const url = `${API_CONFIG.paymentUrl}/api/referrals/me`;
  const response = await fetch(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new Error(data.message || `HTTP error! status: ${response.status}`);
  }
  return data.data || data;
};

/**
 * Apply a friend's referral code to the signed-in customer's account.
 * Only succeeds before the customer's first completed order.
 * @param {string} code - Referral code to apply
 * @param {string} token - Firebase ID token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export const applyReferralCode = async (code, token) => {
  if (!code || !token) throw new Error("Code and token are required");
  const url = `${API_CONFIG.paymentUrl}/api/referrals/apply`;
  const response = await fetch(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify({ code }),
  });
  const text = await response.text();
  const data = text ? JSON.parse(text) : {};
  if (!response.ok) {
    throw new Error(data.message || `HTTP error! status: ${response.status}`);
  }
  return data;
};

/**
 * Register (upsert) this device's FCM push token for the signed-in admin.
 * @param {string} fcmToken - Token returned by initAdminMessaging()
 * @param {string} authToken - Firebase ID token
 * @returns {Promise<Object>}
 */
export const registerAdminFcmToken = async (fcmToken, authToken) => {
  if (!fcmToken || !authToken) throw new Error("FCM token and auth token are required");
  const url = `${API_CONFIG.paymentUrl}/api/payments/admin/fcm-token`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${authToken}` }),
    body: JSON.stringify({ token: fcmToken }),
  });
  return handleResponse(response);
};

/**
 * Fetch recent admin order notifications.
 * @param {string} authToken - Firebase ID token
 * @param {{limit?: number, cursor?: string}} [options]
 * @returns {Promise<{notifications: Object[], unreadCount: number}>}
 */
export const fetchAdminNotifications = async (authToken, { limit = 20, cursor } = {}) => {
  if (!authToken) throw new Error("Auth token is required");
  const params = new URLSearchParams({ limit: String(limit), ...(cursor ? { cursor } : {}) });
  const url = `${API_CONFIG.paymentUrl}/api/payments/admin/notifications?${params}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${authToken}` }),
  });
  const result = await handleResponse(response);
  return result.data || result;
};

/**
 * Mark a single admin notification as read.
 * @param {string} notificationId
 * @param {string} authToken - Firebase ID token
 * @returns {Promise<Object>}
 */
export const markAdminNotificationRead = async (notificationId, authToken) => {
  if (!notificationId || !authToken) throw new Error("Notification ID and auth token are required");
  const url = `${API_CONFIG.paymentUrl}/api/payments/admin/notifications/${notificationId}/read`;
  const response = await fetchWithRetry(url, {
    method: "PATCH",
    headers: createHeaders({ Authorization: `Bearer ${authToken}` }),
  });
  return handleResponse(response);
};

/**
 * Initiate B2B payment for an order
 * @param {Object} paymentData - Payment data
 * @param {string} type - Payment type
 * @param {string} orderId - Order ID
 * @param {string} rid - Rider ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Payment response
 */
export const initiateB2BPayment = async (
  paymentData,
  type,
  orderId,
  rid,
  token
) => {
  if (!paymentData) throw new Error("Payment data is required");

  const url = `${API_CONFIG.paymentUrl}/api/v1/b2b?type=${type}&oid=${orderId}&rid=${rid}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 150000); // 2.5 minutes timeout

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: createHeaders({
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(paymentData),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return handleResponse(response);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === "AbortError") {
      throw new Error("Payment request timeout");
    }
    throw error;
  }
};

/**
 * Submit a store rating
 * @param {string} storeId - Store ID
 * @param {number} rating - Rating value
 * @param {string} uid - User ID
 * @returns {Promise<Object>} Rating response
 */
export const submitStoreRating = async (storeId, rating, uid, token) => {
  if (!storeId || !rating || !uid)
    throw new Error("Store ID, User ID and rating are required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/ratings/add/${storeId}?uid=${uid}`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify({ rating }),
  });
  return handleResponse(response);
};

/**
 * Fetch a user's store rating
 * @param {string} storeId - Store ID
 * @param {string} uid - User ID
 * @returns {Promise<Object>} Rating object
 */
export const fetchUserStoreRating = async (storeId, uid) => {
  if (!storeId || !uid) throw new Error("Store ID and User ID are required");

  const url = `${API_CONFIG.baseUrl}/api/v1/ratings/${storeId}?uid=${uid}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch store rating statistics
 * @param {string} storeId - Store ID
 * @returns {Promise<Object>} Rating stats object
 */
export const fetchStoreRatingStats = async (storeId) => {
  if (!storeId) throw new Error("Store ID is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/ratings/countnaverage?storeId=${storeId}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetch active order for a rider
 * @param {string} rid - Rider ID
 * @param {string} oid - Order ID
 * @returns {Promise<Object>} Active order object
 */
export const fetchActiveOrder = async (rid, oid, token) => {
  if (!rid) throw new Error("Rider ID is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/account/orders/active/${rid}?oid=${oid}`;
  const response = await fetchWithRetry(url, {
    method: "GET",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};

/**
 * Add a new store
 * @param {Object} storeData - Store data
 * @returns {Promise<Object>} Add response
 */
export const addStore = async (storeData, token) => {
  if (!storeData) throw new Error("Store data is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/stores/add`;
  const response = await fetchWithRetry(url, {
    method: "POST",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
    body: JSON.stringify(storeData),
  });
  return handleResponse(response);
};

/**
 * Delete a store
 * @param {string} storeId - Store ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Delete response
 */
export const deleteStore = async (storeId, token) => {
  if (!storeId) throw new Error("Store ID is required");
  if (!token) throw new Error("Auth token is required");

  const url = `${API_CONFIG.baseUrl}/api/v1/stores/${storeId}`;
  const response = await fetchWithRetry(url, {
    method: "DELETE",
    headers: createHeaders({ Authorization: `Bearer ${token}` }),
  });
  return handleResponse(response);
};
